import React, { useState } from 'react';
import {
    Typography,
    Button,
    Box,
    TextField,
    makeStyles,
    useTheme,
    Snackbar
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Email } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS, STEP_TITLES } from '../../constants/wizardSteps.constants';
import { VIEW } from '../../constants/view.constants';
import { helpEmail } from '../../services/api';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
        minHeight: 400,
        width: '100%',
        maxWidth: '550px',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        padding: theme.spacing(5, 2.5),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3, 2),
            gap: theme.spacing(3)
        }
    },
    contentContainer: {
        width: '100%',
        textAlign: 'center',
        marginBottom: theme.spacing(4)
    },
    title: {
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
        fontSize: '24px',
        fontWeight: 600,
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px'
        }
    },
    description: {
        fontSize: '16px',
        lineHeight: 1.6,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px'
        }
    },
    serviceList: {
        paddingLeft: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
        margin: 0,
        listStyleType: 'disc',
        '& li::marker': {
            color: '#9CA3AF'
        }
    },
    listItem: {
        display: 'list-item',
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5)
    },
    listText: {
        fontSize: '14px',
        lineHeight: 1.5,
        color: theme.palette.text.primary,
        margin: 0,
        textAlign: 'left',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px'
        }
    },
    finalDescription: {
        fontSize: '14px',
        lineHeight: 1.5,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        marginBottom: 0,
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px'
        }
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'center',
        flexWrap: 'wrap',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1.5)
        }
    },
    backButton: {
        padding: theme.spacing(1.5, 3),
        backgroundColor: '#ffffff',
        color: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        minWidth: '120px',
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    requestButton: {
        padding: theme.spacing(1.5, 3),
        color: '#FFF',
        minWidth: '150px',
        fontSize: '14px',
        fontWeight: 600,
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2)
    },
    successState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        textAlign: 'center'
    },
    successIcon: {
        fontSize: 48,
        color: '#4CAF50',
        marginBottom: theme.spacing(1)
    },
    successHeader: {
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
        fontSize: '24px',
        fontWeight: 600,
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px'
        }
    },
    successMessage: {
        fontSize: '16px',
        lineHeight: 1.6,
        color: theme.palette.text.primary,
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px'
        }
    },
    bottomSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#F8F9FA',
        borderRadius: theme.spacing(1),
        border: '1px solid #E5E7EB'
    },
    bottomMessage: {
        fontSize: '14px',
        lineHeight: 1.5,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px'
        }
    },
    envelopeContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2)
    },
    animatedEnvelope: {
        fontSize: 64,
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            fontSize: 48
        }
    },
    emailSection: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    emailField: {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#D1D5DB'
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px'
            }
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0
        }
    }
}));

const serviceFeatures = [
    "Review and optimize your content for better AI performance",
    "Fine-tune your agent's responses and behavior", 
    "Provide training on advanced features and best practices",
    "Offer ongoing support and maintenance recommendations"
];

export default function WhiteGloveServiceStep({ checkTokenExpiration }) {
    const classes = useStyles();
    const theme = useTheme();
    const [{ wizardData, previousStep }, dispatch] = useStateValue();
    
    const [requestStatus, setRequestStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [showToast, setShowToast] = useState(false);
    const [email, setEmail] = useState(wizardData.userDetails?.email || '');
    const [emailError, setEmailError] = useState('');

    // Check if user has email in context
    const hasUserEmail = wizardData.userDetails?.email && wizardData.userDetails.email.trim() !== '';

    const validateEmail = (email) => {
        if (!email.trim()) {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const handleBack = () => {
        // Handle special case for SUCCESS view
        if (previousStep === 'SUCCESS') {
            dispatch({
                type: 'SET_VIEW',
                view: VIEW.SUCCESS
            });
        } else {
            // Navigate back to the previous step or default to NAME_YOUR_AGENT
            const targetStep = previousStep || WIZARD_STEPS.NAME_YOUR_AGENT;
            dispatch({
                type: 'SET_CURRENT_STEP',
                step: targetStep
            });
        }
        // Clear the previous step
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: null
        });
    };

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setEmail(value);
        // Always validate email on change for real-time feedback
        const error = validateEmail(value);
        setEmailError(error);
    };

    const handleSendRequest = async () => {
        // Validate email if user doesn't have one in context
        if (!hasUserEmail) {
            const emailValidationError = validateEmail(email);
            if (emailValidationError) {
                setEmailError(emailValidationError);
                return;
            }
        }

        setRequestStatus('loading');
        setShowToast(true);
        
        try {
            const userEmail = hasUserEmail ? wizardData.userDetails.email : email;
            
            // Determine the step context for better support
            let stepContext = 'Unknown';
            if (previousStep === VIEW.SUCCESS) {
                stepContext = 'Agent Created Successfully (Success Page)';
            } else if (previousStep && STEP_TITLES[previousStep]) {
                stepContext = STEP_TITLES[previousStep];
            } else if (previousStep) {
                stepContext = `Step ${previousStep}`;
            } else {
                // Fallback: Try to determine context from wizard data and current state
                if (wizardData.isPublished || wizardData.chatbotId) {
                    stepContext = 'Agent Created Successfully (Success Page)';
                } else if (wizardData.chatbotName || wizardData.skillDescription || wizardData.files?.length > 0) {
                    stepContext = 'Wizard In Progress';
                } else {
                    // If all wizard data is empty, user is likely at the beginning
                    stepContext = 'Name Your Agent (Start of Wizard)';
                }
            }
            
            // Prepare help request payload with context
            const helpRequestData = {
                email: userEmail,
                currentStep: stepContext,
                message: `Help requested at step: ${stepContext}`,
                agentName: wizardData.chatbotName || 'Not provided',
                agentId: wizardData.chatbotId || 'Not created yet'
            };
            
            console.log('Sending help request with context:', helpRequestData);
            
            // Call the real help email API with step context
            await helpEmail(helpRequestData);
            
            setRequestStatus('success');
            
        } catch (error) {
            console.error('Error sending help request:', error);
            setRequestStatus('error');
            setShowToast(false);
        }
    };

    const handleCloseToast = () => {
        setShowToast(false);
    };

    // Check if form is valid for button enable/disable
    const isFormValid = hasUserEmail || (email.trim() && !validateEmail(email));

    if (requestStatus === 'loading') {
        return (
            <Box className={classes.stepContainer}>
                <Box className={classes.loadingState}>
                    <Typography variant="h6" className={classes.title}>
                        Sending Your Request...
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Please wait while we submit your White Glove Service request.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (requestStatus === 'success') {
        return (
            <Box className={classes.stepContainer}>
                <Box className={classes.successState}>
                    {/* Main Success Content */}
                    <Box style={{ marginBottom: theme.spacing(2.5) }}>
                        <Typography className={classes.successHeader}>
                            Request White Glove Service Initiated
                        </Typography>
                        <Typography className={classes.successMessage}>
                            Thank you for contacting us! <br />
                            The support request email has been successfully sent.
                        </Typography>
                    </Box>
                    
                    {/* Bottom Section */}
                    <Box className={classes.bottomSection}>
                        <Typography className={classes.bottomMessage}>
                            One of our customer success agents will reach out to you shortly to help you get onboarded.
                        </Typography>
                        <Typography className={classes.bottomMessage}>
                            We're dedicated to making your onboarding experience seamless and exceptional!
                        </Typography>
                    </Box>
                    
                    {/* Animated Envelope */}
                    <Box className={classes.envelopeContainer}>
                        <Email className={classes.animatedEnvelope} />
                    </Box>
                </Box>
            </Box>
        );
    }

    if (requestStatus === 'error') {
        return (
            <Box className={classes.stepContainer}>
                <Box className={classes.contentContainer}>
                    <Typography variant="h6" className={classes.title} style={{ color: '#f44336' }}>
                        Request Failed
                    </Typography>
                    <Typography variant="body1" color="textSecondary" style={{ marginBottom: theme.spacing(3) }}>
                        We're sorry, but we couldn't send your request at this time. Please try again later.
                    </Typography>
                </Box>
                
                <Box className={classes.buttonContainer}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        className={classes.backButton}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendRequest}
                        className={classes.requestButton}
                    >
                        Try again
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box className={classes.stepContainer}>
                <Box className={classes.contentContainer}>
                    <Typography variant="h4" className={classes.title}>
                        White Glove Service
                    </Typography>
                    
                    <Typography className={classes.description}>
                        Our White Glove Service provides personalized support to help you get the most out of your AI agent. 
                        Our experts will work with you to:
                    </Typography>
                    
                    <Box component="ul" className={classes.serviceList}>
                        {serviceFeatures.map((feature, index) => (
                            <Box key={index} component="li" className={classes.listItem}>
                                <Typography className={classes.listText}>
                                    {feature}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    
                    <Typography className={classes.finalDescription}>
                        Once you submit your request, our team will contact you within 24 hours to schedule a consultation 
                        and discuss how we can help optimize your AI agent for your specific needs.
                    </Typography>
                </Box>
                
                {/* Email Input Section - Show only if user doesn't have email */}
                {!hasUserEmail && (
                    <Box className={classes.emailSection}>
                        <Typography variant="body2" style={{ marginBottom: theme.spacing(1), color: theme.palette.text.secondary }}>
                            Please provide your email address so we can contact you:
                        </Typography>
                        <TextField
                            className={classes.emailField}
                            label="Email Address"
                            variant="outlined"
                            fullWidth
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            error={!!emailError}
                            helperText={emailError}
                            placeholder="your.email@example.com"
                            required
                        />
                    </Box>
                )}
                
                <Box className={classes.buttonContainer}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        className={classes.backButton}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendRequest}
                        className={classes.requestButton}
                        disabled={!isFormValid}
                    >
                        Send request
                    </Button>
                </Box>
            </Box>
            
            {/* Toast Notification */}
            <Snackbar
                open={showToast && requestStatus === 'loading'}
                autoHideDuration={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    severity="info" 
                    onClose={handleCloseToast}
                    style={{ 
                        backgroundColor: '#E3F2FD',
                        color: '#1976D2',
                        border: '1px solid #90CAF9'
                    }}
                >
                    Sending your White Glove Service request...
                </Alert>
            </Snackbar>
        </>
    );
}
